/** Building blocks shared by the admin pages. */
import { Ellipsis } from "lucide-react";
import { useState, type ReactNode } from "react";
import { ContextMenu, CopyBox, Modal, Spinner, type MenuItem } from "../components/ui";
import { navigate } from "../lib/app";

/** An in-app link that can carry aria attributes, which lib/app's Link can't. */
export function AdminLink({
  to,
  className,
  title,
  current,
  children,
}: {
  to: string;
  className?: string;
  title?: string;
  current?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={to}
      className={className}
      title={title}
      aria-current={current ? "page" : undefined}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

export function PageHead({ title, lede, actions }: { title: string; lede?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="admin-head">
      <div className="admin-head-text">
        <h1>{title}</h1>
        {lede && <p className="admin-lede">{lede}</p>}
      </div>
      {actions && <div className="admin-head-actions">{actions}</div>}
    </header>
  );
}

/** A titled panel. `flush` lets a table run edge to edge. */
export function Card({
  title,
  sub,
  actions,
  flush,
  className = "",
  children,
}: {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`admin-card${flush ? " flush" : ""} ${className}`}>
      {(title || actions) && (
        <div className="admin-card-head">
          <div className="admin-card-title">
            {title && <h2>{title}</h2>}
            {sub && <p>{sub}</p>}
          </div>
          {actions && <div className="admin-card-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export type Tone = "ok" | "warn" | "bad";

/** One headline figure. With `to`, the whole tile links to the page that explains it. */
export function Stat({ label, value, sub, to, children }: { label: string; value: ReactNode; sub?: ReactNode; to?: string; children?: ReactNode }) {
  const body = (
    <>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {children}
      {sub && <span className="stat-sub">{sub}</span>}
    </>
  );
  return to ? (
    <AdminLink to={to} className="stat stat-link">
      {body}
    </AdminLink>
  ) : (
    <div className="stat">{body}</div>
  );
}

/** A share of a limit: the fill turns amber, then red, as it fills. */
export function Meter({ value, tone = "ok", label }: { value: number; tone?: Tone; label: string }) {
  return (
    <span className={`meter meter-${tone}`} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value * 100)} aria-label={label}>
      <span style={{ width: `${Math.min(100, Math.max(value > 0 ? 1.5 : 0, value * 100))}%` }} />
    </span>
  );
}

export function Switch({ on, label, title, busy, onChange }: { on: boolean; label: string; title?: string; busy?: boolean; onChange: () => void }) {
  return (
    <span className="admin-switch-wrap">
      {busy && <Spinner size={12} />}
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        title={title}
        className={`admin-switch${on ? " on" : ""}`}
        disabled={busy}
        onClick={onChange}
      >
        <span className="admin-switch-knob" />
      </button>
    </span>
  );
}

/** The "…" button at the end of a row, opening a menu of everything else that can be done to it. */
export function RowMenu({ label, items }: { label: string; items: Array<MenuItem | "-"> }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  return (
    <>
      <button
        type="button"
        className={`icon-btn row-menu-btn${at ? " on" : ""}`}
        aria-label={label}
        aria-haspopup="menu"
        title={label}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setAt({ x: r.right - 236, y: r.bottom + 4 });
        }}
      >
        <Ellipsis size={16} />
      </button>
      {at && <ContextMenu x={at.x} y={at.y} items={items} onClose={() => setAt(null)} />}
    </>
  );
}

export interface ConfirmOptions {
  title: string;
  body: ReactNode;
  confirm: string;
  danger?: boolean;
}

/** A confirm() that looks like the rest of the app: `ask` resolves true when they go ahead. */
export function useConfirm(): [ReactNode, (o: ConfirmOptions) => Promise<boolean>] {
  const [open, setOpen] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
  const close = (ok: boolean) => {
    open?.resolve(ok);
    setOpen(null);
  };
  const dialog = open && (
    <Modal
      title={open.title}
      onClose={() => close(false)}
      width={440}
      footer={
        <>
          <button className="btn" onClick={() => close(false)}>
            Cancel
          </button>
          <button className={`btn ${open.danger ? "btn-danger-solid" : "btn-primary"}`} onClick={() => close(true)} autoFocus>
            {open.confirm}
          </button>
        </>
      }
    >
      <div className="admin-confirm">{open.body}</div>
    </Modal>
  );
  return [dialog, (o) => new Promise((resolve) => setOpen({ ...o, resolve }))];
}

/** Shows a sign-up or reset link once, to copy and send. */
export function LinkDialog({ title, url, note, onClose }: { title: string; url: string; note?: ReactNode; onClose: () => void }) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      width={520}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="stack">
        <CopyBox value={url} />
        <p className="muted small" style={{ margin: 0 }}>
          This is the only time the link is shown. Send it privately{note ? "; " : "."}
          {note}
        </p>
      </div>
    </Modal>
  );
}

/** A row of mutually exclusive filters, each with a count. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: string; count?: number; tone?: Tone }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={value === o.value ? "on" : ""}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined && <span className={`segmented-count${o.tone ? ` tone-${o.tone}` : ""}`}>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Loading({ children }: { children: ReactNode }) {
  return (
    <p className="admin-loading">
      <Spinner /> {children}
    </p>
  );
}

/** A callout inside a page: something to know or fix. */
export function Notice({ tone = "info", icon, children, action }: { tone?: "info" | Tone; icon?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={`admin-notice notice-${tone}`}>
      {icon && <span className="admin-notice-icon">{icon}</span>}
      <div className="admin-notice-body">{children}</div>
      {action && <div className="admin-notice-action">{action}</div>}
    </div>
  );
}
